// React Hooks:
import React, { useState, useEffect } from "react";
// React-Router-Dom:
import { useNavigate, useParams } from "react-router-dom";
// Redux Toolkit Hooks:
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
// Redux Toolkit Actions & State:
import {
  getTargetInfoAction,
  checkPasswordFileAction,
  resetDownloadStatusAction,
  download,
} from "../downloadSlice";
// Formik Framework:
import { useFormik } from "formik";
// Yup for validation:
import * as yup from "yup";
// MUI Components:
import {
  Grid,
  Box,
  Paper,
  Typography,
  Button,
  SvgIcon,
  TextField,
  InputAdornment,
  Divider,
  useTheme,
  Skeleton,
} from "@mui/material";
import { useSnackbar } from "notistack";
// MUI Styles:
import { makeStyles } from "@mui/styles";
// MUI Icones:
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
// Logo Icon:
import logoIcon from "../../../assets/images/megaDL-Logo2.png";
// Icones:
import { ReactComponent as KeyIcon } from "../../../assets/icons/key-icon.svg";
import { ReactComponent as AlertIcon } from "../../../assets/icons/alert-icon.svg";
import { ReactComponent as CheckIcon } from "../../../assets/icons/check-circle-icon.svg";

const useStyles = makeStyles((theme) => ({
  downloadContainer: {
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    zIndex: 1,
    boxShadow:
      "5px 5px 10px 0px rgb(0 0 0 / 20%), -2px 3px 10px 0px rgb(0 0 0 / 15%), 0px -2px 10px 0px rgb(0 0 0 / 15%)",
    overflowY: "auto",
    overflowX: "hidden",
    "&::-webkit-scrollbar": {
      borderRadius: "10px",
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: "10px",
      backdropFilter: "blur(4px)",
      backgroundColor: "rgba(28, 33, 45, 0.4)",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "10px",
      backgroundColor: theme.palette.secondary.main,
    },
  },
  logo: {
    width: "auto",
    height: "85px",
    [theme.breakpoints.down("sm")]: {
      height: "55px",
    },
  },
  form: {
    width: "295px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      width: "70%",
    },
    [theme.breakpoints.down(460)]: {
      width: "80%",
    },
    [theme.breakpoints.down(320)]: {
      width: "100%",
    },
  },
  field: {
    borderRadius: "5px",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[4],
    "& label.Mui-focused": {
      color: theme.palette.text.primary,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: theme.palette.background.paper,
    },
    "& .MuiOutlinedInput-root": {
      paddingLeft: theme.spacing(0),
      "& fieldset": {
        borderColor: theme.palette.background.paper,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.background.paper,
        borderWidth: 1.5,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.background.paper,
        borderWidth: 1.5,
      },
    },
  },
  text: {
    color: theme.palette.text.secondary,
    zIndex: 1,
  },
}));

const DownloadBox = () => {
  const { enqueueSnackbar } = useSnackbar();

  const classes = useStyles();
  const theme = useTheme();

  const params = useParams();

  const dispatch = useAppDispatch();

  const {
    downloadData: {
      actionStatus,
      alert: { error, message },
      fileName,
      idChacked,
      isPassword,
      hash,
      id,
      redirect,
    },
  } = useAppSelector(download);

  const [showPass, setShowPass] = useState({
    password: false,
  });

  const validationSchema = yup.object({
    password: yup.string().required("Please enter your password"),
  });

  const formik = useFormik({
    initialValues: {
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      dispatch(
        checkPasswordFileAction({
          uniq_id: params.id,
          id: id,
          password: values.password,
          hash: hash,
        })
      );
    },
  });

  // const showPosition = (position: any) => {
  //   console.log(position);
  //   console.log(new Date(position.timestamp).toLocaleDateString("fa-IR"));
  // };

  // const getLocation = () => {
  //   if (navigator.geolocation) {
  //     navigator.geolocation.getCurrentPosition(showPosition);
  //   }
  // };

  // getLocation();

  // useEffect(() => {
  //   if (params.id?.length === 8) {
  //     dispatch(getTargetInfoAction({ id: params.id }));
  //   } else {
  //     enqueueSnackbar("Your download address is not correct!", {
  //       variant: "error",
  //     });
  //   }
  // }, [dispatch, enqueueSnackbar, params.id]);

  useEffect(() => {
    dispatch(getTargetInfoAction({ uniq_id: params.id, id: params.id3 }));
  }, [dispatch, params.id, params.id3]);

  useEffect(() => {
    if (redirect) {
      window.close();
      enqueueSnackbar("Download started", {
        variant: "success",
      });
    }
  }, [enqueueSnackbar, redirect]);

  useEffect(() => {
    switch (message) {
      case "noid":
        enqueueSnackbar("A page for your download address was not found!", {
          variant: "error",
        });
        break;

      case "hashError":
        enqueueSnackbar("Your download has timed out!", {
          variant: "error",
        });
        break;

      case "errorCount":
        // enqueueSnackbar("Your count of downloads is over!", {
        //   variant: "error",
        // });
        break;

      case "errorPass":
        enqueueSnackbar("The password file is not correct!", {
          variant: "error",
        });
        break;

      default:
        break;
    }
  }, [enqueueSnackbar, message]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        p: "24px",
        alignItems: "center",
      }}
    >
      {/* message === "errorCount" ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography variant="h1" className={classes.text}>
            403
          </Typography>
          <Typography
            variant="h5"
            sx={{ mt: 2, mb: 4 }}
            className={classes.text}
          >
            The number of allowed downloads has been reached !
          </Typography>
        </Box>
      ) :  */}
      {message === "noid" || message === "errorCount" ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography variant="h1" className={classes.text}>
            404
          </Typography>
          <Typography
            variant="h5"
            sx={{ mt: 2, mb: 4 }}
            className={classes.text}
          >
            Page not found !
          </Typography>
        </Box>
      ) : redirect ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography variant="h1" className={classes.text}>
            200
          </Typography>
          <Typography
            variant="h5"
            sx={{ mt: 2, mb: 4 }}
            className={classes.text}
          >
            Downloading
          </Typography>
        </Box>
      ) : idChacked === false || actionStatus === "loading" ? (
        <Grid
          container
          sx={{
            width: "525px",
            minHeight: "fit-content",
            maxHeight: "90%",
            [theme.breakpoints.down("sm")]: {
              width: "100%",
            },
          }}
          className={classes.downloadContainer}
        >
          <Grid
            item
            xs={12}
            sx={{
              width: "100%",
              height: "370px",
              p: "16px 32px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
              color: theme.palette.text.secondary,
              [theme.breakpoints.down("sm")]: {
                height: "100%",
              },
            }}
          >
            <Skeleton
              variant="circular"
              width={"100px"}
              height={"100px"}
              animation="wave"
              sx={{
                bgcolor: "#e8e8e8",
              }}
            />
            <Skeleton
              variant="rounded"
              width={"70%"}
              height={"104px"}
              animation="wave"
              sx={{
                bgcolor: "#e8e8e8",
              }}
            />
            <Divider sx={{ width: "100%" }} />
            <Skeleton
              variant="rounded"
              width={"100%"}
              height={"102px"}
              animation="wave"
              sx={{
                bgcolor: "#e8e8e8",
              }}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "90px",
              [theme.breakpoints.down("sm")]: {
                minHeight: "fit-content",
              },
            }}
          >
            <Paper
              sx={{
                width: "100%",
                height: "100%",
                p: "16px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
              elevation={5}
            >
              <Skeleton
                variant="rounded"
                width={"235px"}
                height={"48px"}
                animation="wave"
                sx={{
                  bgcolor: "#e8e8e8",
                }}
              />
            </Paper>
          </Grid>
        </Grid>
      ) : (
        <Grid
          container
          sx={{
            width: "525px",
            minHeight: "fit-content",
            maxHeight: "90%",
            [theme.breakpoints.down("sm")]: {
              width: "100%",
            },
          }}
          className={classes.downloadContainer}
        >
          <Grid
            item
            xs={12}
            sx={{
              width: "100%",
              height: "400px",
              p: "32px 32px 16px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
              color: theme.palette.text.secondary,
              [theme.breakpoints.down("sm")]: {
                height: "100%",
              },
            }}
          >
            <Box
              component="img"
              src={logoIcon}
              alt="Mega DL Logo"
              className={classes.logo}
            />

            <Box
              sx={{
                p: "16px",
                width: "100%",
                textAlign: "center",
                [theme.breakpoints.down("sm")]: {
                  p: "8px",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "24px",
                  mb: "8px",
                  fontWeight: "bolder",
                  color: "inherit",
                  [theme.breakpoints.down("sm")]: {
                    fontSize: "18px",
                    mb: "4px",
                  },
                }}
              >
                {fileName}
              </Typography>

              <Typography
                sx={{
                  fontSize: "19px",
                  color: "inherit",
                  [theme.breakpoints.down("sm")]: {
                    fontSize: "12px",
                  },
                }}
              >
                Expires in 7 days
              </Typography>
            </Box>

            <Divider sx={{ width: "100%" }} />

            <Box
              sx={{
                p: "16px",
                width: "100%",
                textAlign: "left",
                [theme.breakpoints.down("sm")]: {
                  p: "8px",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "17px",
                  fontWeight: "lighter",
                  color: "inherit",
                  [theme.breakpoints.down("sm")]: {
                    fontSize: "11px",
                  },
                }}
              >
                Computer file, a resource for storing information. file URI
                scheme · file (command), a Unix program for determining the type
                of data contained in a computer
              </Typography>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              minHeight: "90px",
              [theme.breakpoints.down("sm")]: {
                minHeight: "fit-content",
              },
            }}
          >
            <Paper
              sx={{
                width: "100%",
                height: "100%",
                p: "16px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
              elevation={5}
            >
              {isPassword ? (
                <Box
                  component="form"
                  onSubmit={formik.handleSubmit}
                  className={classes.form}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "flex-end",
                      padding: "8px 14px",
                    }}
                  >
                    <SvgIcon
                      component={KeyIcon}
                      inheritViewBox
                      sx={{
                        width: "22px",
                        height: "18px",
                        color: theme.palette.text.secondary,
                        mr: "10px",
                        [theme.breakpoints.down("sm")]: {
                          width: "18px",
                        },
                      }}
                    />
                    <Typography
                      sx={{
                        lineHeight: "1",
                        color: theme.palette.text.secondary,
                        [theme.breakpoints.down("sm")]: {
                          fontSize: "14px",
                          lineHeight: "normal",
                        },
                      }}
                    >
                      Password
                    </Typography>
                  </Box>
                  <TextField
                    name="password"
                    placeholder="Passsword"
                    type={showPass.password ? "text" : "password"}
                    value={formik.values.password}
                    onChange={(e: any) => {
                      formik.handleChange(e);
                      dispatch(resetDownloadStatusAction());
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.password && Boolean(formik.errors.password)
                    }
                    size={"small"}
                    sx={{
                      label: { color: theme.palette.text.secondary },
                      input: {
                        padding: "14px",
                        paddingLeft: "0px",
                        color: theme.palette.text.secondary,
                        "&::placeholder": {
                          color: theme.palette.text.secondary,
                          opacity: "0.5",
                        },
                        "&:-webkit-autofill": {
                          WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset`,
                          WebkitTextFillColor: theme.palette.text.secondary,
                          transition:
                            " background-color 600000s 0s, color 600000s 0s",
                        },
                        [theme.breakpoints.down("sm")]: {
                          padding: "12px",
                        },
                      },
                      marginBottom: "16px",
                      width: "100%",
                      [theme.breakpoints.down("sm")]: {
                        marginBottom: "10px",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          sx={{ marginLeft: "14px", marginRight: "10px" }}
                        >
                          <SvgIcon
                            component={
                              showPass.password
                                ? VisibilityOffIcon
                                : VisibilityIcon
                            }
                            inheritViewBox
                            sx={{
                              width: "22px",
                              cursor: "pointer",
                              color: theme.palette.text.secondary,
                              opacity: "0.5",
                              [theme.breakpoints.down("sm")]: {
                                width: "20px",
                              },
                            }}
                            onClick={() => {
                              setShowPass((last) => ({
                                ...last,
                                password: !last.password,
                              }));
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    className={classes.field}
                  />

                  {formik.touched.password && formik.errors.password ? (
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "flex-start",
                        mt: "-6px",
                      }}
                    >
                      <SvgIcon
                        component={AlertIcon}
                        inheritViewBox
                        sx={{
                          width: "20px",
                          color: "#E36464",
                          m: "0px 10px 14px 14px",
                          [theme.breakpoints.down("sm")]: {
                            width: "16px",
                            m: "0px 10px 8px 8px",
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          width: "100%",
                          fontSize: "12px",
                          lineHeight: "2.5",
                          color: "#E36464",
                          [theme.breakpoints.down("sm")]: {
                            fontSize: "11px",
                          },
                        }}
                      >
                        {formik.errors.password}
                      </Typography>
                    </Box>
                  ) : error === true && message === "errorPass" ? (
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "flex-start",
                        mt: "-6px",
                      }}
                    >
                      <SvgIcon
                        component={AlertIcon}
                        inheritViewBox
                        sx={{
                          width: "20px",
                          color: "#E36464",
                          m: "0px 10px 14px 14px",
                          [theme.breakpoints.down("sm")]: {
                            width: "16px",
                            m: "0px 10px 8px 8px",
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          width: "100%",
                          fontSize: "12px",
                          lineHeight: "2.5",
                          color: "#E36464",
                          [theme.breakpoints.down("sm")]: {
                            fontSize: "11px",
                          },
                        }}
                      >
                        The password file is not correct!
                      </Typography>
                    </Box>
                  ) : error === true && message === "hashError" ? (
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "flex-start",
                        mt: "-6px",
                      }}
                    >
                      <SvgIcon
                        component={AlertIcon}
                        inheritViewBox
                        sx={{
                          width: "20px",
                          color: "#E36464",
                          m: "0px 10px 14px 14px",
                          [theme.breakpoints.down("sm")]: {
                            width: "16px",
                            m: "0px 10px 8px 8px",
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          width: "100%",
                          fontSize: "12px",
                          lineHeight: "2.5",
                          color: "#E36464",
                          [theme.breakpoints.down("sm")]: {
                            fontSize: "11px",
                          },
                        }}
                      >
                        Your download has timed out!
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="h1" className={classes.text}>
                        200
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{ mt: 2, mb: 4 }}
                        className={classes.text}
                      >
                        Downloading
                      </Typography>
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={!!formik.values.password ? false : true}
                    fullWidth
                    type="submit"
                    sx={{
                      width: "235px",
                      fontSize: "18px",
                      borderRadius: "8px",
                      textTransform: "none",
                      lineHeight: "0px",
                      padding: "24px",
                      ":disabled": {
                        backgroundColor: "#C5C5C5",
                        color: theme.palette.text.primary,
                      },
                      [theme.breakpoints.down("sm")]: {
                        fontSize: "16px",
                        padding: "22px",
                        width: "80%",
                      },
                    }}
                  >
                    Download
                  </Button>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  type="submit"
                  onClick={() =>
                    dispatch(
                      checkPasswordFileAction({
                        uniq_id: params.id,
                        id: id,
                        hash: hash,
                      })
                    )
                  }
                  sx={{
                    width: "235px",
                    fontSize: "18px",
                    borderRadius: "8px",
                    textTransform: "none",
                    lineHeight: "0px",
                    padding: "24px",
                    ":disabled": {
                      backgroundColor: "#C5C5C5",
                      color: theme.palette.text.primary,
                    },
                    [theme.breakpoints.down("sm")]: {
                      fontSize: "16px",
                      padding: "22px",
                      width: "80%",
                    },
                  }}
                >
                  Download
                </Button>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default DownloadBox;
